import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { InfoIcon } from 'lucide-react';
import { borrower, loanOfficer, scenarios } from '../../data/quote';
import { easeOut } from '../../utils/motion';
import { ExampleBadge } from '../ExampleBadge';
import { HomeSummary } from './HomeSummary';
import { ScenarioCard } from './ScenarioCard';
import { LoanOfficerSignature } from './LoanOfficerSignature';
type QuoteEmailProps = {
  compare: boolean;
};
export function QuoteEmail({
  compare
}: QuoteEmailProps) {
  const reduce = useReducedMotion();
  const visible = compare ? [scenarios[1], scenarios[0]] : [scenarios[0]];
  return <motion.article aria-label="Purchase quote email preview" initial={reduce ? false : {
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: easeOut
  }} className={`mx-auto w-full overflow-hidden rounded-[32px] bg-white shadow-soft ${compare ? 'max-w-[880px]' : 'max-w-[680px]'}`} data-id="element-110" data-mp="vfz4bd1podb">
      <header className="bg-navy px-6 pb-9 pt-7 text-white sm:px-10" data-id="element-111" data-mp="39dadeex463ej">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-112" data-mp="34fsb37dlqmfi">
          <p className="text-sm font-bold text-white/70" data-id="element-113" data-mp="30cvq5a0lae8e">
            Prepared for {borrower.name} · {borrower.preparedOn}
          </p>
          <ExampleBadge data-id="element-114" data-mp="2vd9aktxaglwf" />
        </div>
        <h2 className="mt-6 text-[34px] font-black leading-[1.05] tracking-tight sm:text-[44px]" data-id="element-115" data-mp="2vgdjg4lp85tf">
          Your Purchase Quote
        </h2>
        <p className="mt-3 max-w-lg text-base font-semibold text-white/80" data-id="element-116" data-mp="2lnzktkpx3fbh">
          Hi {borrower.firstName}, here's a first look at what your monthly payment and cash to close could be.
          {compare ? ' I put two programs side by side so you can compare.' : ''}
        </p>
      </header>

      <div className="space-y-8 px-5 py-7 sm:px-10 sm:py-9" data-id="element-117" data-mp="27oq2aee8o94c">
        <HomeSummary data-id="element-118" data-mp="27qazycvuo86f" />

        <div className={`grid gap-4 ${compare ? 'md:grid-cols-2' : ''}`} data-id="element-119" data-mp="22otyaf5jkhos">
          <AnimatePresence initial={false} mode="popLayout" data-id="element-120" data-mp="1t1rcenv15mrq">
            {visible.map((scenario, index) => <motion.div key={scenario.id} layout={reduce ? false : 'position'} initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={reduce ? undefined : {
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.24,
            ease: easeOut
          }} data-id="element-121" data-mp="1y0ksmo1pbrvl" data-mpi={index}>
                <ScenarioCard scenario={scenario} framed={compare} label={compare ? `Option ${String.fromCharCode(65 + index)}` : 'Your loan program'} data-id="element-122" data-mp="2grc8m8g72giy" />
              </motion.div>)}
          </AnimatePresence>
        </div>

        <LoanOfficerSignature data-id="element-123" data-mp="2ur3ipy3lbcro" />

        <section aria-label="Important disclosures" className="rounded-[24px] bg-canvas p-5" data-id="element-124" data-mp="2uq263p6xei7u">
          <p className="flex items-center gap-2 text-sm font-extrabold" data-id="element-125" data-mp="2zi3bqs9l9aow">
            <InfoIcon className="h-4 w-4" aria-hidden="true" data-id="element-126" data-mp="o2l12ny7f5pd" />
            The fine print
          </p>
          <p className="mt-2 text-xs font-semibold leading-relaxed text-navy-500" data-id="element-127" data-mp="j41b8cghp9rq">
            This quote is an estimate only. It is not a loan offer, a commitment to lend, or a rate lock, and it is not a
            Loan Estimate. Your actual rate, APR, payment, and closing costs may differ and depend on credit approval,
            property eligibility, appraisal, and market conditions at the time you apply. Property taxes, insurance, and
            mortgage insurance are estimates. {loanOfficer.company}, {loanOfficer.nmls}. Equal Housing Lender.
          </p>
        </section>
      </div>

      <footer className="relative overflow-hidden border-t-2 border-dashed border-navy-100 bg-canvas px-6 py-7 text-center" data-id="element-128" data-mp="a3bijhmizzi8">
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 flex select-none items-center justify-center whitespace-nowrap text-[72px] font-black tracking-tight text-navy/[0.05] -rotate-3" data-id="element-129" data-mp="1piskp4joi2xd">
          EXAMPLE DATA
        </span>
        <p className="relative mx-auto max-w-md text-xs font-bold text-navy-500" data-id="element-130" data-mp="2ktgx40oi2352">
          EXAMPLE data — every figure in this email is a static placeholder for layout review. Nothing here is
          calculated.
        </p>
      </footer>
    </motion.article>;
}